import { useQuery } from '@apollo/client'
import { Autocomplete, TextField, type AutocompleteProps } from '@mui/material'
import {
    CandidateFilterCategory,
    SearchIndexField,
    type GetIndustryOptionsQuery,
} from '__generated__/graphql'
import { useMemo, useState } from 'react'
import { FilterWrapper } from 'recruiter/components/FilterWrapper'
import { GetIndustryOptions } from 'recruiter/graphql/companies.gql'
import { useRosterSearchFilter } from 'recruiter/hooks'

export function RosterIndustryFilter() {
    const [open, setOpen] = useState(false)
    const { value, setValue } = useRosterSearchFilter(
        SearchIndexField.RosterIndustries
    )
    const [industryOptions, setIndustryOptions] = useState<
        GetIndustryOptionsQuery['industries']
    >([])

    const { loading: areIndustryOptionsLoading } = useQuery(
        GetIndustryOptions,
        {
            onCompleted(data) {
                setIndustryOptions(data?.industries ?? [])
            },
        }
    )

    const industriesInFilter = useMemo(() => {
        const industryIdsInFilter = value?.value?.stringArray ?? []
        return industryOptions.filter(({ id }) =>
            industryIdsInFilter.includes(id)
        )
    }, [industryOptions, value])

    const handleIndustryChange: AutocompleteProps<
        (typeof industryOptions)[number],
        true,
        false,
        false
    >['onChange'] = (e, newValues) => {
        if (newValues.length === 0) {
            setValue(null)
            return
        }

        setValue({
            category: CandidateFilterCategory.All,
            value: { stringArray: newValues.map(({ id }) => id) },
        })
    }

    return (
        <FilterWrapper label="Industry">
            <Autocomplete
                ChipProps={{ size: 'small' }}
                loading={areIndustryOptionsLoading}
                multiple
                onChange={handleIndustryChange}
                getOptionLabel={({ value }) => value}
                getOptionKey={({ id }) => id}
                options={industryOptions}
                renderInput={(params) => (
                    <TextField {...params} placeholder="Search industries..." />
                )}
                value={industriesInFilter}
                open={open}
                onOpen={() => setOpen(true)}
                onClose={(_event, reason) => {
                    if (reason === 'blur') {
                        setOpen(false)
                    }
                }}
            />
        </FilterWrapper>
    )
}
